import {readPackagingSuppliers, salesSkuRelations} from './salesSupplierSource.mjs';
import {rfqCategory} from './rfqProductCatalog.mjs';
export const BUSINESS_UNIT = 'empaque';
export const BUSINESS_UNITS = [['empaque', 'Amevel Empaques'], ['coffee_vending', 'Coffee & Vending'], ['foods', 'Amevel Foods']];
export const businessUnitName = unit => BUSINESS_UNITS.find(([id]) => id === unit)?.[1] || unit;
export const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
export function locationFor(supplier) {
    if (supplier.country) return supplier.country;
    const address = normalize([supplier.city, supplier.address].filter(Boolean).join(' '));
    const matches = [['China', /\bchina\b/], ['El Salvador', /\bel salvador\b/], ['Perú', /\bperu\b/]].filter(([, pattern]) => pattern.test(address));
    return matches.length === 1 ? matches[0][0] : '';
}
export const leadTimeFor = supplier => ({china: 90, 'el salvador': 45, peru: 45}[normalize(locationFor(supplier))] ?? null);
export function categoryFor(product) {
    const grouped = rfqCategory(product.category);
    if (grouped) return {name: grouped, basis: 'Agrupación de subcategorías para cotización'};
    if (/^(vasos?|tapas?|vasos?\s+y\s+tapas?)$/.test(normalize(product.category))) return {name: 'Vasos y Tapas', basis: 'Agrupación unificada del catálogo'};
    if (product.category) return {name: product.category, basis: 'Catálogo Syncro'};
    const name = product.name || '';
    if (/\b(?:vasos?|tapas?)\b/.test(normalize(name.split(':').pop()))) return {name: 'Vasos y Tapas', basis: 'Agrupación visual por descripción'};
    const path = name.split(':');
    if (path.slice(0, -1).some(part => /\bvasos?\s+y\s+tapas?\b/.test(normalize(part)))) return {name: 'Vasos y Tapas', basis: 'Jerarquía sincronizada unificada'};
    if (path.length > 2) return {name: rfqCategory(path[1]) || path[1], basis: 'Jerarquía sincronizada'};
    return {name: 'Sin subcategoría', basis: 'Pendiente de clasificación'};
}
// A bounded endpoint is read until its first short page. A guard is an error, never silent truncation.
export async function readPages(api, path, size) {
    const all = [], seen = new Set();
    for (let offset = 0; offset <= 100000; offset += size) {
        const page = await api(`${path}&offset=${offset}`);
        if (!Array.isArray(page)) throw new Error('Respuesta de catálogo inválida.');
        for (const record of page) {
            if (!record._id || seen.has(String(record._id))) throw new Error('Paginación inestable: recarga el catálogo.');
            seen.add(String(record._id)); all.push(record);
        }
        if (page.length < size) return all;
    }
    throw new Error('Catálogo fuera del límite de consulta; no se puede declarar completo.');
}
export async function loadSourcing(api, businessUnit = BUSINESS_UNIT) {
    if (!BUSINESS_UNITS.some(([id]) => id === businessUnit)) throw new Error('Unidad de negocio inválida.');
    const [products, suppliers, quickbooks, consolidated] = await Promise.all([
        readPages(api, `products?businessUnit=${businessUnit}&limit=500`, 500),
        readPages(api, `suppliers?businessUnit=${businessUnit}&limit=500`, 500),
        readPages(api, `quickbooks-catalog?businessUnit=${businessUnit}&type=item`, 200),
        api(`draft-sourcing-relations?businessUnit=${businessUnit}`)
    ]);
    if (products.some(p => p.businessUnit !== businessUnit) || suppliers.some(s => !s.businessUnits?.includes(businessUnit)) || quickbooks.some(p => p.businessUnit !== businessUnit)) throw new Error(`Respuesta fuera de ${businessUnitName(businessUnit)}; se descartó la carga.`);
    if (consolidated.businessUnit !== businessUnit || consolidated.truncated || !Array.isArray(consolidated.relations) || !Array.isArray(consolidated.warnings)) throw new Error('Relaciones incompletas o fuera de la unidad seleccionada.');
    const known = new Set(products.map(p => String(p._id))), supplierIds = new Set(suppliers.map(s => String(s._id)));
    const relations = consolidated.relations.filter(r => known.has(String(r.product)) && supplierIds.has(String(r.supplier)));
    const warnings = consolidated.warnings;
    const linked = new Set(products.flatMap(p => (p.qbLinks || []).map(l => JSON.stringify([l.realmId, l.quickBooksId]))));
    const pending = quickbooks.filter(q => !['Category', 'Group'].includes(q.itemType) && q.active !== false && !linked.has(JSON.stringify([q.realmId, q.quickBooksId])));
    return {businessUnit, products, suppliers, quickbooks, relations, pending, warnings: [...new Set(warnings)], loadedAt: new Date().toISOString()};
}

// Only Empaques switches supplier identity, attributes and relationships to Ventas.
// The inventory/SKU and accounting catalogs retain their existing sources.
export async function loadSalesSourcing(api, crm) {
    const businessUnit = BUSINESS_UNIT;
    const [products, quickbooks, directory] = await Promise.all([
        readPages(api, `products?businessUnit=${businessUnit}&limit=500`, 500),
        readPages(api, `quickbooks-catalog?businessUnit=${businessUnit}&type=item`, 200),
        readPackagingSuppliers(crm)
    ]);
    if (products.some(p => p.businessUnit !== businessUnit) || quickbooks.some(p => p.businessUnit !== businessUnit)) throw new Error('Respuesta fuera de Amevel Empaques; se descartó la carga.');
    const linked = new Set(products.flatMap(p => (p.qbLinks || []).map(l => JSON.stringify([l.realmId, l.quickBooksId]))));
    const pending = quickbooks.filter(q => !['Category', 'Group'].includes(q.itemType) && q.active !== false && !linked.has(JSON.stringify([q.realmId, q.quickBooksId])));
    return {businessUnit, supplierSource: 'sales', products, suppliers: directory.suppliers, quickbooks,
        relations: salesSkuRelations(directory.suppliers, products, p => categoryFor(p).name),
        pending, warnings: directory.warnings, loadedAt: new Date().toISOString()};
}
